import PropTypes from 'prop-types';
import Theme from '@adsk/alloy-react-theme';
import { BlockRemoveButton, UnstyledAnchor } from '@adsk/acc-reference-picker';
import { useCurrentToolId } from '@adsk/alloy-provider';
import { genDeepLinkUrl } from '../helpers/genDeepLinkUrl';
import { useDueDateTimezone } from '../../../hooks';
import { AssigneeCell, StatusCell } from '../../../components';
import { isLate, toLongDate, translate } from '../../../helpers';
import { Rfi } from '../../../sdk';

export type RfiEntityBlockProps = {
  entity: Rfi;
  projectId: Rfi['projectId'];
  onRemoveClick?: (id: Rfi['id'], projectId: Rfi['projectId']) => void;
  onClick: (entity: Rfi) => void;
  onReferenceClickAnalytics: () => void;
};
const RfiEntityBlock = ({
  entity,
  projectId,
  onRemoveClick,
  onClick,
  onReferenceClickAnalytics,
}: RfiEntityBlockProps) => {
  const {
    id,
    customIdentifier,
    title,
    assignedTo,
    status: rfiStatus,
    dueDate,
    closedAt,
    createdAt,
  } = entity;
  const { dueDateTimeZone } = useDueDateTimezone(createdAt);
  const currentToolId = useCurrentToolId();

  const isClosed = !!closedAt;
  const url = genDeepLinkUrl({ projectId, rfiId: id, currentToolId });
  const handleClick = () => {
    onReferenceClickAnalytics?.();
    onClick?.(entity);
  };

  return (
    <UnstyledAnchor
      style={[
        {
          display: 'flex',
          flex: 1,
          justifyContent: 'space-between',
          padding: '10px 17px',
          borderBottom: `solid 1px ${Theme.colors.charcoal100}`,
          minHeight: '81px',
          cursor: 'pointer',
          outline: 'none',
          overflow: 'hidden',

          ...(onRemoveClick && {
            '.rfiEntityBlock__remove-button': {
              visibility: 'hidden',
            },
            '&:hover .rfiEntityBlock__remove-button': {
              visibility: 'visible',
            },
          }),
        },
      ]}
      onClick={handleClick}
      href={url}
      role="button"
      tabIndex={0}
    >
      <div
        css={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'space-between',
          color: Theme.colors.charcoal900,
          width: '100%',
          minWidth: 0, // Makes the flex shrink and show ellipsis correctly
        }}
      >
        <header
          css={{
            ...Theme.typography.bodyMediumBold,
            WebkitLineClamp: 2,
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
          }}
        >
          {customIdentifier && <span css={{ marginRight: '8px' }}>{`#${customIdentifier}`}</span>}
          <span>{title}</span>
        </header>
        <span css={{ ...Theme.typography.bodySmall, padding: '6px 0' }}>
          <AssigneeCell assignees={assignedTo} variant={AssigneeCell.VARIANTS.TABLE_CELL} />
        </span>
        <div
          css={{
            ...Theme.typography.bodySmall,
            display: 'flex',
          }}
        >
          <StatusCell status={rfiStatus} stacked={false} />
          {dueDate && (
            <span
              css={{
                marginLeft: '9px',
                paddingLeft: '12px',
                borderLeft: `solid 1px ${Theme.colors.charcoal400}`,
                ...(!isClosed &&
                  isLate(dueDate) && {
                    color: Theme.colors.red500,
                  }),
              }}
            >
              {translate('rpPlugin.due', {
                dueDate: toLongDate(dueDate, dueDateTimeZone),
              })}
            </span>
          )}
        </div>
      </div>
      <div css={{ display: 'flex', alignItems: 'center' }}>
        {onRemoveClick && (
          <BlockRemoveButton
            className="rfiEntityBlock__remove-button"
            onClick={() => onRemoveClick(id, projectId)}
          />
        )}
      </div>
    </UnstyledAnchor>
  );
};

RfiEntityBlock.displayName = 'RfiEntityBlock';
RfiEntityBlock.defaultProps = {
  onRemoveClick: undefined,
  onClick: undefined,
};
RfiEntityBlock.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    customIdentifier: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    assignedTo: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
      }),
    ),
    status: PropTypes.string.isRequired,
    dueDate: PropTypes.string,
    closedAt: PropTypes.string,
    createdAt: PropTypes.string,
  }).isRequired,
  onRemoveClick: PropTypes.func,
  onClick: PropTypes.func,
  onReferenceClickAnalytics: PropTypes.func,
  projectId: PropTypes.string.isRequired,
};

export default RfiEntityBlock;
